import { createContext, useContext, useState } from 'react';
import { 
  initializeAuth, 
  getStoredUser, 
  getStoredToken,
  login as authLogin, 
  register as authRegister,
  registerWithInvite as authRegisterWithInvite,
  logout as authLogout
} from '../services/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage synchronously
  const [user, setUser] = useState(() => {
    const hasAuth = initializeAuth();
    if (hasAuth) {
      return getStoredUser();
    }
    return null;
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!getStoredToken();
  });
  
  const [loading, setLoading] = useState(false);

  const login = async (email, password) => {
    try {
      console.log('[AuthContext] Calling login...');
      const response = await authLogin(email, password);
      console.log('[AuthContext] Login response:', response);
      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        return { success: true };
      }
      return { success: false, error: response.error || response.detail || response.message || 'Login failed' };
    } catch (error) {
      console.error('[AuthContext] Login error:', error);
      const message = error.response?.data?.error || error.response?.data?.detail || error.message || 'Login failed. Please try again.';
      return { success: false, error: message };
    }
  };

  const register = async (email, password, name, phone = null) => {
    try {
      const response = await authRegister(email, password, name, phone);
      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        // No reload needed - React state update will handle re-render
        return { success: true };
      }
      return { success: false, error: 'Registration failed' };
    } catch (error) {
      // Backend returns error in 'error' field, not 'detail'
      const message = error.response?.data?.error || error.response?.data?.detail || 'Registration failed. Please try again.';
      return { success: false, error: message };
    }
  };

  const registerWithInvite = async (inviteCode, email, password, name, phone = null) => {
    try {
      const response = await authRegisterWithInvite(inviteCode, email, password, name, phone);
      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        // No reload needed - React state update will handle re-render
        return { success: true };
      }
      return { success: false, error: 'Registration failed' };
    } catch (error) {
      // Backend returns error in 'error' field, not 'detail'
      const message = error.response?.data?.error || error.response?.data?.detail || 'Registration failed. Please try again.';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    authLogout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (newUserData) => {
    setUser(prev => ({ ...prev, ...newUserData }));
    // Update localStorage too
    const storedUser = getStoredUser();
    if (storedUser) {
      localStorage.setItem('fira_user', JSON.stringify({ ...storedUser, ...newUserData }));
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    registerWithInvite,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
